import { useCallback, useState, useEffect } from "react";
import { useQuery } from "react-query";

import SearchBar from "../components/Search";
import UserListTable from "../components/UserList/UserListTable";
import { useRegion } from "../contexts/RegionContext";
import { getUsers, SearchQuery } from "../services/userServices";
import { UsersType } from "../types/user";
import PaginationComponent from "../components/Pagination";
import { PAGE_SIZE_DEFAULT } from "../constants";

import styles from "../styles/users.module.scss";

const Users = () => {
  const [searchQuery, setSearchQuery] = useState<SearchQuery>();
  const [search, setSearch] = useState("");
  const { selectedRegion } = useRegion();

  const [currentPage, setCurrentPage] = useState(1);

  const { isLoading, error, data } = useQuery<UsersType, Error>(
    ["users", currentPage, searchQuery],
    async () =>
      await getUsers({
        page: currentPage,
        pageSize: PAGE_SIZE_DEFAULT,
        searchQuery,
      })
  );

  // User data is already filtered server-side based on the admin's regional access
  // permissions. The frontend only adds the appCountry parameter when the user explicitly
  // selects a specific region from the dropdown.
  const addRegionToQuery = useCallback(
    (baseQuery: SearchQuery | undefined) => {
      const { appCountry, ...rest } = baseQuery || {};
      return selectedRegion
        ? { ...rest, appCountry: selectedRegion }
        : Object.keys(rest).length > 0
        ? rest
        : undefined;
    },
    [selectedRegion]
  );

  const onClickSearch = useCallback(
    (query) => {
      setCurrentPage(1);
      setSearchQuery(addRegionToQuery(query));
    },
    [addRegionToQuery]
  );

  useEffect(() => {
    setCurrentPage(1);
    setSearchQuery((prevQuery) => addRegionToQuery(prevQuery));
  }, [selectedRegion, addRegionToQuery]);

  return (
    <div>
      <h1 className={styles.pageTitle}>Users</h1>
      <SearchBar
        searchQuery={searchQuery}
        setSearchQuery={onClickSearch}
        resultFound={data?.pageInfo?.totalCount}
        search={search}
        setSearch={setSearch}
      />
      <div className={styles.container}>
        {data?.pageInfo && (
          <PaginationComponent
            pageCount={Math.ceil(
              data.pageInfo.totalCount / data.pageInfo.pageSize
            )}
            currentPage={currentPage}
            itemOfPage={data.users.length}
            totalItems={data.pageInfo.totalCount}
            containerClass={styles.userListTablePagination}
            setCurrentPage={setCurrentPage}
          />
        )}
        <UserListTable
          search={search}
          data={data}
          error={error}
          isLoading={isLoading}
        />
        {data?.pageInfo && (
          <PaginationComponent
            pageCount={Math.ceil(
              data.pageInfo.totalCount / data.pageInfo.pageSize
            )}
            currentPage={currentPage}
            itemOfPage={data.users.length}
            totalItems={data.pageInfo.totalCount}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
};

export default Users;
